import { useState, useEffect, useRef } from 'react';
import { getLayoutClasses, detectOrientation, detectMobile, detectTablet, detectMobileOrTablet } from '../utils/layoutUtils';

/**
 * Custom hook to manage responsive layout state
 * @param {string} pathname - Current route pathname
 * @param {boolean} sidebarVisible - Whether the sidebar component exists
 * @returns {Object} - Layout state and handlers
 */
const useResponsiveLayout = (pathname, sidebarVisible = true) => {
  // Core layout state
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [isMobile, setIsMobile] = useState(detectMobile());
  const [isTablet, setIsTablet] = useState(detectTablet());
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(detectMobileOrTablet());
  const [currentOrientation, setCurrentOrientation] = useState(detectOrientation());
  const [focusModeLeftSpace, setFocusModeLeftSpace] = useState(() =>
    getComputedStyle(document.documentElement).getPropertyValue('--focus-mode-left-space').trim() || '20px'
  );
  const [normalModeLeftSpace, setNormalModeLeftSpace] = useState(() =>
    getComputedStyle(document.documentElement).getPropertyValue('--normal-mode-left-space').trim() || '0px'
  );
  const [layoutClasses, setLayoutClasses] = useState(
    getLayoutClasses(isSidebarOpen, isFocusMode, isMobile, focusModeLeftSpace, normalModeLeftSpace, sidebarVisible)
  );
  const [layoutKey, setLayoutKey] = useState(0);
  const prevOrientationRef = useRef(currentOrientation);

  // Update layout classes when layout state changes
  useEffect(() => {
    setLayoutClasses(getLayoutClasses(isSidebarOpen, isFocusMode, isMobile, focusModeLeftSpace, normalModeLeftSpace, sidebarVisible));
    // Only force child components to update for significant layout changes
    // Don't trigger remount for sidebar toggle to preserve ongoing requests
  }, [isSidebarOpen, isFocusMode, isMobile, focusModeLeftSpace, normalModeLeftSpace, currentOrientation, sidebarVisible]);

  // Separate effect for layout key updates - only for orientation and focus mode changes
  useEffect(() => {
    setLayoutKey(prevKey => prevKey + 1);
  }, [isFocusMode, currentOrientation]); // Removed isSidebarOpen and other deps

  // Handle window resize and orientation changes
  useEffect(() => {
    const handleResize = () => {
      // Detect mobile/tablet/desktop
      const mobile = detectMobile();
      const tablet = detectTablet();
      const mobileOrTablet = detectMobileOrTablet();
      const mobileChanged = isMobile !== mobile;
      const tabletChanged = isTablet !== tablet;
      const mobileOrTabletChanged = isMobileOrTablet !== mobileOrTablet;

      // Detect orientation
      const newOrientation = detectOrientation();
      const orientationChanged = newOrientation !== currentOrientation;

      // Update states if needed
      if (mobileChanged) {
        setIsMobile(mobile);
      }
      if (tabletChanged) {
        setIsTablet(tablet);
      }
      if (mobileOrTabletChanged) {
        setIsMobileOrTablet(mobileOrTablet);
      }

      if (orientationChanged) {
        setCurrentOrientation(newOrientation);
        prevOrientationRef.current = newOrientation;

        // Update data attribute on document
        document.documentElement.dataset.lastOrientation = newOrientation;
      }
    };

    // Register event listeners
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    // Initial setup
    handleResize();
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isMobile, isTablet, isMobileOrTablet, currentOrientation]);

  // Set focus mode based on route
  useEffect(() => {
    if (pathname === "/editor") {
      setIsFocusMode(true);
    } else if (pathname === "/chatty") {
      // Keep current state for Chatty
    } else {
      setIsFocusMode(false);
    }
  }, [pathname]);

  // Add/remove focus-mode class from body
  useEffect(() => {
    if (isFocusMode) {
      document.body.classList.add('focus-mode');
    } else {
      document.body.classList.remove('focus-mode');
    }
    return () => {
      document.body.classList.remove('focus-mode'); // Cleanup on unmount
    };
  }, [isFocusMode]);

  // Helper to close sidebar when clicking on content (mobile only)
  useEffect(() => {
    const contentContainer = document.querySelector('.content-container');
    if (!contentContainer) return;
    
    const handleContentClick = (e) => {
      if (isSidebarOpen) {
        // Don't collapse if clicking on interactive elements
        if (!e.target.closest('button') && 
            !e.target.closest('a') && 
            !e.target.closest('input') && 
            !e.target.closest('textarea')) {
          setIsSidebarOpen(false);
        }
      }
    };
    
    // Add the event listener
    contentContainer.addEventListener('click', handleContentClick);
    
    // Clean up
    return () => {
      contentContainer.removeEventListener('click', handleContentClick);
    };
  }, [isMobile, currentOrientation, isSidebarOpen]);

  // Handlers
  const handleToggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const handleToggleFocusMode = () => setIsFocusMode(!isFocusMode);
  
  const collapseSidebar = () => setIsSidebarOpen(false);

  return {
    // State
    isSidebarOpen,
    setIsSidebarOpen,
    isFocusMode,
    setIsFocusMode,
    isMobile,
    isTablet,
    isMobileOrTablet, // Use this for touch-friendly behaviors (navbar, gestures)
    currentOrientation,
    focusModeLeftSpace,
    setFocusModeLeftSpace,
    normalModeLeftSpace,
    setNormalModeLeftSpace,
    layoutClasses,
    layoutKey,

    // Handlers
    handleToggleSidebar,
    handleToggleFocusMode,
    collapseSidebar
  };
};

export default useResponsiveLayout;